import Head from 'next/head'

// VideoObject structured data for the speaker profile's MEDIA section.
//
// The players (react-player / react-vimeo) mount their <video>/<iframe>
// imperatively after hydration, and only ever for the one selected video, so the
// initial HTML contains no crawlable video markup. This schema is what exposes
// every video on the page to search engines, so it must be built during render
// from the SSR-seeded props -- never from an effect.

// The /speakerProfile payload now returns `uploadDate` directly on each video
// (backed by the Contentful Video entry). Prefer that. Fall back to reading the
// Cloudinary asset version stamp out of the delivery URL
// (".../v1720011858/...") for any video the API hasn't backfilled yet -- that's
// genuinely when the file was uploaded to the CDN, so it's a safe stand-in.
const CLOUDINARY_VERSION = /\/v(\d{9,10})\//

const toUploadDate = (video) => {
  if (video?.uploadDate) {
    const fromApi = new Date(video.uploadDate)
    if (!Number.isNaN(fromApi.getTime())) return fromApi.toISOString()
  }
  const stamp = video?.url?.match(CLOUDINARY_VERSION)?.[1]
  if (!stamp) return null
  const date = new Date(Number(stamp) * 1000)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

// Contentful asset URLs are protocol-relative ("//images.ctfassets.net/...").
const toAbsoluteUrl = (url) => (url?.startsWith('//') ? `https:${url}` : url)

// Keeps a "</script>" inside any title or description from terminating the tag.
const toSafeJson = (value) => JSON.stringify(value).replace(/</g, '\\u003c')

export const buildVideoNodes = (videos = [], speakerName = '') =>
  videos
    .map((video) => {
      const name = video?.fields?.title
      const thumbnailUrl = toAbsoluteUrl(video?.fields?.file?.url)
      const contentUrl = video?.url
      const uploadDate = toUploadDate(video)

      // name, thumbnailUrl and uploadDate are required by Google, and a
      // VideoObject with no contentUrl gives crawlers no video to index. A node
      // missing any of them is a validation error rather than a partial win, so
      // drop it -- some speakers do carry a video with an empty url.
      if (!name || !thumbnailUrl || !contentUrl || !uploadDate) return null

      return {
        '@type': 'VideoObject',
        name,
        // fields.description belongs to the thumbnail asset, not the video, and
        // is empty on ~97% of entries, so fall back to a derived sentence.
        description: video.fields.description || `${name} — video featuring ${speakerName}.`,
        thumbnailUrl,
        uploadDate,
        contentUrl,
      }
    })
    .filter(Boolean)

const VideoSchema = ({ videos = [], speakerName = '' }) => {
  const nodes = buildVideoNodes(videos, speakerName)
  if (!nodes.length) return null

  return (
    <Head>
      <script
        type="application/ld+json"
        key="speaker-video-schema"
        dangerouslySetInnerHTML={{
          __html: toSafeJson({ '@context': 'https://schema.org', '@graph': nodes }),
        }}
      />
    </Head>
  )
}

export default VideoSchema
